/**
 * richDiff.js — HTML-aware tracked changes for wiki edits.
 *
 * Creates a single formatted HTML document with inline tracked changes,
 * similar to MS Word's Track Changes:
 *   - Added text wrapped in <ins class="tc-ins">
 *   - Removed text wrapped in <del class="tc-del"> (contenteditable="false")
 *   - Unchanged text preserved with its original HTML formatting
 *
 * The output uses the NEW HTML's tag structure as the base, with
 * removed text inserted inline at the appropriate positions.
 */

/**
 * Normalize ampersand encoding in text content.
 * Converts "&amp;" to "&" so that content with different encoding compares equal.
 * Only handles ampersands - other entities like &lt; should remain encoded
 * to preserve intentional escaping of HTML characters.
 */
function normalizeAmpersands(html) {
  if (!html) return '';
  // Only decode &amp; in text content, not inside HTML tags
  return html.replace(/(<[^>]+>)|([^<]+)/g, (match, tag, text) => {
    if (tag) return tag; // Keep tags as-is
    // Decode &amp; to & in text content
    return text.replace(/&amp;/g, '&');
  });
}

/**
 * Tokenize HTML into an array of tokens:
 *   { type: 'tag', value: '<p>' }
 *   { type: 'word', value: 'hello' }
 *   { type: 'ws', value: ' ' }
 */
function tokenizeHtml(html) {
  if (!html) return [];
  const tokens = [];
  const re = /(<[^>]+>)|([^<]+)/g;
  let match;
  while ((match = re.exec(html)) !== null) {
    if (match[1]) {
      tokens.push({ type: 'tag', value: match[1] });
    } else if (match[2]) {
      const text = match[2];
      const parts = text.split(/(\s+)/);
      for (const part of parts) {
        if (!part) continue;
        if (/^\s+$/.test(part)) {
          tokens.push({ type: 'ws', value: part });
        } else {
          tokens.push({ type: 'word', value: part });
        }
      }
    }
  }
  return tokens;
}

/**
 * LCS-based word diff.
 * Returns a flat array of { type: 'equal'|'added'|'removed', value: string }
 * where value is the raw HTML word token.
 */
function computeWordDiff(oldWords, newWords) {
  const m = oldWords.length;
  const n = newWords.length;

  if (m === 0 && n === 0) return [];
  if (m === 0) return newWords.map(w => ({ type: 'added', value: w }));
  if (n === 0) return oldWords.map(w => ({ type: 'removed', value: w }));

  // Build LCS table
  const dp = [];
  for (let i = 0; i <= m; i++) {
    dp[i] = new Uint16Array(n + 1);
  }
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build diff
  const raw = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      raw.push({ type: 'equal', value: oldWords[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.push({ type: 'added', value: newWords[j - 1] });
      j--;
    } else {
      raw.push({ type: 'removed', value: oldWords[i - 1] });
      i--;
    }
  }
  raw.reverse();
  return raw;
}

/**
 * Character-level LCS diff between two strings.
 * Returns array of { type: 'equal'|'added'|'removed', value: char }.
 */
function computeCharDiff(oldStr, newStr) {
  const m = oldStr.length;
  const n = newStr.length;
  if (m === 0 && n === 0) return [];
  if (m === 0) return newStr.split('').map(c => ({ type: 'added', value: c }));
  if (n === 0) return oldStr.split('').map(c => ({ type: 'removed', value: c }));

  const dp = [];
  for (let i = 0; i <= m; i++) dp[i] = new Uint16Array(n + 1);
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldStr[i - 1] === newStr[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const raw = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldStr[i - 1] === newStr[j - 1]) {
      raw.push({ type: 'equal', value: oldStr[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      raw.push({ type: 'added', value: newStr[j - 1] });
      j--;
    } else {
      raw.push({ type: 'removed', value: oldStr[i - 1] });
      i--;
    }
  }
  raw.reverse();
  return raw;
}

/**
 * Try to produce a character-level diff between two words.
 * Returns an HTML string with inline <del>/<ins> for individual characters,
 * or null if the words aren't similar enough (falls back to word-level).
 */
function tryCharDiff(oldWord, newWord) {
  const maxLen = Math.max(oldWord.length, newWord.length);
  if (maxLen <= 1) return null;

  const diff = computeCharDiff(oldWord, newWord);
  const equalCount = diff.filter(d => d.type === 'equal').length;

  // Only use char-level diff if words share >= 40% of characters
  if (equalCount / maxLen < 0.4) return null;

  let html = '';
  let idx = 0;
  while (idx < diff.length) {
    const type = diff[idx].type;
    let text = '';
    while (idx < diff.length && diff[idx].type === type) {
      text += diff[idx].value;
      idx++;
    }
    if (type === 'equal') {
      html += text;
    } else if (type === 'removed') {
      html += `<del class="tc-del" contenteditable="false">${text}</del>`;
    } else {
      html += `<ins class="tc-ins">${text}</ins>`;
    }
  }
  return html;
}

/**
 * Create a tracked-changes HTML string from two HTML documents.
 *
 * The result preserves the NEW HTML's formatting/structure and adds
 * <del class="tc-del"> for removed text, <ins class="tc-ins"> for added text.
 *
 * @param {string} oldHtml — The previous/original HTML content
 * @param {string} newHtml — The proposed/new HTML content
 * @returns {string} Merged HTML with tracked changes markers
 */
export function createTrackedChangesHtml(oldHtml, newHtml) {
  const clean = h => (h || '').replace(/^__RAW_HTML__/, '').trim();
  // Normalize ampersand encoding so "&" vs "&amp;" doesn't show as a change
  const oldClean = normalizeAmpersands(clean(oldHtml));
  const newClean = normalizeAmpersands(clean(newHtml));

  if (!oldClean && !newClean) return '<p><em>No content</em></p>';
  if (!oldClean) {
    // Everything is new
    return newClean.replace(/(<[^>]+>)|([^<]+)/g, (match, tag, text) => {
      if (tag) return tag;
      return `<ins class="tc-ins">${text}</ins>`;
    });
  }
  if (!newClean) {
    return oldClean.replace(/(<[^>]+>)|([^<]+)/g, (match, tag, text) => {
      if (tag) return tag;
      return `<del class="tc-del" contenteditable="false">${text}</del>`;
    });
  }

  const oldTokens = tokenizeHtml(oldClean);
  const newTokens = tokenizeHtml(newClean);

  const oldWords = oldTokens.filter(t => t.type === 'word').map(t => t.value);
  const newWords = newTokens.filter(t => t.type === 'word').map(t => t.value);

  // Compute word diff
  const diff = computeWordDiff(oldWords, newWords);

  // Build output by walking newTokens and consuming diff entries.
  // Invariant: 'equal' and 'added' entries correspond 1:1 with newWords (in order).
  //            'removed' entries don't correspond to any new word and are flushed
  //            before the next equal/added entry.
  // Closing block tags that should have pending removals flushed inside them
  // so that deleted content appears within the block, not orphaned after </p> etc.
  const CLOSING_BLOCK_RE = /^<\/(p|div|li|ul|ol|h[1-6]|blockquote|pre|table|tr|td|th|thead|tbody|tfoot)\b/i;

  let output = '';
  let diffIdx = 0;

  for (const token of newTokens) {
    if (token.type === 'ws') {
      output += token.value;
    } else if (token.type === 'tag') {
      // Before a closing block tag, flush any pending removed entries so that
      // deleted words appear inside the block rather than after it.
      if (CLOSING_BLOCK_RE.test(token.value)) {
        const removedWords = [];
        while (diffIdx < diff.length && diff[diffIdx].type === 'removed') {
          removedWords.push(diff[diffIdx].value);
          diffIdx++;
        }
        if (removedWords.length > 0) {
          output += `<del class="tc-del" contenteditable="false">${removedWords.join(' ')}</del>`;
        }
      }
      output += token.value;
    } else if (token.type === 'word') {
      // Flush pending 'removed' entries before this word
      const removedWords = [];
      while (diffIdx < diff.length && diff[diffIdx].type === 'removed') {
        removedWords.push(diff[diffIdx].value);
        diffIdx++;
      }

      // Handle current diff entry (should be 'equal' or 'added')
      if (diffIdx < diff.length) {
        if (diff[diffIdx].type === 'added') {
          // Try character-level diff for 1:1 removed→added pair
          if (removedWords.length === 1) {
            const charHtml = tryCharDiff(removedWords[0], token.value);
            if (charHtml) {
              output += charHtml;
              diffIdx++;
            } else {
              output += `<del class="tc-del" contenteditable="false">${removedWords[0]}</del> `;
              output += `<ins class="tc-ins">${token.value}</ins>`;
              diffIdx++;
            }
          } else {
            if (removedWords.length > 0) {
              output += `<del class="tc-del" contenteditable="false">${removedWords.join(' ')}</del> `;
            }
            output += `<ins class="tc-ins">${token.value}</ins>`;
            diffIdx++;
          }
        } else {
          // 'equal' — output removed words then the equal word
          if (removedWords.length > 0) {
            output += `<del class="tc-del" contenteditable="false">${removedWords.join(' ')}</del> `;
          }
          output += token.value;
          diffIdx++;
        }
      } else {
        if (removedWords.length > 0) {
          output += `<del class="tc-del" contenteditable="false">${removedWords.join(' ')}</del> `;
        }
        output += token.value;
      }
    }
  }

  // Flush any remaining removed entries at the end
  const trailingRemoved = [];
  while (diffIdx < diff.length) {
    if (diff[diffIdx].type === 'removed') {
      trailingRemoved.push(diff[diffIdx].value);
    }
    diffIdx++;
  }
  if (trailingRemoved.length > 0) {
    output += ` <del class="tc-del" contenteditable="false">${trailingRemoved.join(' ')}</del>`;
  }

  return output;
}

/**
 * Extract the "clean" content from tracked changes HTML.
 * Removes <del> elements (and their content) and unwraps <ins> tags
 * (keeping their content). The result is the reviewer's intended content.
 */
export function extractCleanContent(html) {
  if (!html) return '';
  // Remove <del> elements entirely (including content)
  let clean = html.replace(/<del\b[^>]*>[\s\S]*?<\/del>/gi, '');
  // Remove <ins> tags but keep their content
  clean = clean.replace(/<ins\b[^>]*>([\s\S]*?)<\/ins>/gi, '$1');
  // Clean up double spaces left from removed dels
  clean = clean.replace(/ {2,}/g, ' ');
  return clean;
}

/**
 * Count additions and removals in tracked changes HTML.
 */
export function countChanges(trackedHtml) {
  if (!trackedHtml) return { additions: 0, removals: 0 };
  const ins = (trackedHtml.match(/<ins\b/g) || []).length;
  const del = (trackedHtml.match(/<del\b/g) || []).length;
  return { additions: ins, removals: del };
}
